import { useRef, useState, type ReactElement } from 'react';

import {
  TotpEnrollmentDialog,
  type TotpEnrollmentDialogProps,
} from '@/components/shared/totp-enrollment-dialog';
import { useAuthStore } from '@/stores/auth-store';

export interface UseTotpEnrollmentGateOptions {
  /** Default explanation when opening the enrollment dialog. */
  description?: string;
}

/**
 * Opens in-place two-factor enrollment instead of sending people to Account.
 * `requireTotpThen` runs the action after setup; `openEnrollment` accepts a one-off callback.
 */
export const useTotpEnrollmentGate = (
  options: UseTotpEnrollmentGateOptions = {},
): {
  isTotpEnabled: boolean;
  requireTotpThen: (action: () => void, description?: string) => void;
  openEnrollment: (next?: { description?: string; onEnrolled?: () => void }) => void;
  dialog: ReactElement<TotpEnrollmentDialogProps>;
} => {
  const isTotpEnabled = useAuthStore((state) => state.user?.isTotpEnabled ?? false);
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState<string | undefined>(options.description);
  const pendingRef = useRef<(() => void) | null>(null);

  const openEnrollment = (next?: { description?: string; onEnrolled?: () => void }) => {
    if (next?.description !== undefined) setDescription(next.description);
    pendingRef.current = next?.onEnrolled ?? null;
    setOpen(true);
  };

  const requireTotpThen = (action: () => void, nextDescription?: string) => {
    if (isTotpEnabled) {
      action();
      return;
    }
    openEnrollment({ description: nextDescription, onEnrolled: action });
  };

  const handleEnrolled = () => {
    setOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.();
  };

  const dialog = (
    <TotpEnrollmentDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) pendingRef.current = null;
        setOpen(next);
      }}
      description={description}
      autoStart
      onEnrolled={handleEnrolled}
    />
  );

  return { isTotpEnabled, requireTotpThen, openEnrollment, dialog };
};
